// Extrait de DataTab.js (handover bloc F1) — JSX, textes et appels API inchangés.
import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Download, Target, Plus, Trash2, Play, Save } from "lucide-react";

export default function CohortBuilderView() {
  const [fields, setFields] = useState(null);
  const [conditions, setConditions] = useState([{ field: "", operator: "eq", value: "" }]);
  const [logic, setLogic] = useState("AND");
  const [cohortName, setCohortName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/cohort/fields").then(res => setFields(res.data)).catch(() => {});
  }, []);

  const allFields = fields
    ? [
        ...fields.patient_fields.map(f => ({ ...f, group: "Patient" })),
        ...fields.symptom_fields.map(f => ({ ...f, group: "Symptomes" })),
        ...fields.vital_fields.map(f => ({ ...f, group: "Constantes" })),
        ...fields.alert_fields.map(f => ({ ...f, group: "Alertes" })),
        ...(fields.lab_fields || []).map(f => ({ ...f, group: "Biologie" })),
        ...(fields.derived_fields || []).map(f => ({ ...f, group: "Parcours" })),
      ]
    : [];

  const getDefaultOperatorForField = (fieldName) => {
    const info = allFields.find(f => f.field === fieldName);
    return info?.type === "number" ? "gte" : "eq";
  };

  const getOperatorsForField = (fieldName) => {
    const info = allFields.find(f => f.field === fieldName);
    const ops = fields?.operators || [];
    if (!info) return ops.filter(op => op.value === "eq");
    if (info.type === "number") return ops.filter(op => ["eq", "neq", "gte", "lte", "gt", "lt"].includes(op.value));
    if (info.type === "select") return ops.filter(op => ["eq", "neq", "in"].includes(op.value));
    return ops.filter(op => ["eq", "neq", "contains"].includes(op.value));
  };

  const addCondition = () => {
    setConditions([...conditions, { field: "", operator: "eq", value: "" }]);
  };

  const removeCondition = (idx) => {
    setConditions(conditions.filter((_, i) => i !== idx));
  };

  const updateCondition = (idx, key, val) => {
    setConditions(conditions.map((c, i) => {
      if (i !== idx) return c;
      if (key === "field") {
        return { ...c, field: val, operator: getDefaultOperatorForField(val), value: "" };
      }
      return { ...c, [key]: val };
    }));
  };

  const runPreview = async () => {
    const validConditions = conditions.filter(c => c.field && c.value !== "");
    if (validConditions.length === 0) {
      toast.error("Ajoutez au moins une condition");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/cohort/preview", {
        name: cohortName || "Cohorte",
        conditions: validConditions,
        logic,
        start_date: startDate || null,
        end_date: endDate || null,
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const exportCohort = async (format) => {
    const validConditions = conditions.filter(c => c.field && c.value !== "");
    setExporting(true);
    try {
      const res = await api.post(`/cohort/export?format=${format}`, {
        name: cohortName || "Cohorte",
        description: `Cohorte generee le ${new Date().toLocaleDateString("fr-FR")}`,
        conditions: validConditions,
        logic,
        start_date: startDate || null,
        end_date: endDate || null,
      }, { responseType: "blob" });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cohort_${(cohortName || "export").replace(/\s/g, "_")}.${format === "json" ? "json" : "csv"}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} telecharge`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur export");
    } finally {
      setExporting(false);
    }
  };

  const exportWithSummary = async (format) => {
    const validConditions = conditions.filter(c => c.field && c.value !== "");
    setExporting(true);
    try {
      const res = await api.post(`/cohort/export-with-summary?format=${format}`, {
        name: cohortName || "Cohorte",
        description: `Cohorte generee le ${new Date().toLocaleDateString("fr-FR")}`,
        conditions: validConditions,
        logic,
        start_date: startDate || null,
        end_date: endDate || null,
      }, { responseType: "blob" });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cohort_${(cohortName || "export").replace(/\s/g, "_")}_package.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Package Export + Summary (ZIP) telecharge");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur export");
    } finally {
      setExporting(false);
    }
  };

  const getFieldInfo = (fieldName) => allFields.find(f => f.field === fieldName);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="dash-card bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-600" />
              Cohort Builder
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Construisez des cohortes sur mesure avec des criteres combinables
            </p>
          </div>
          <input
            type="text"
            value={cohortName}
            onChange={e => setCohortName(e.target.value)}
            placeholder="Nom de la cohorte..."
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-64"
            data-testid="cohort-name-input"
          />
        </div>
      </div>

      {/* Conditions Builder */}
      <div className="dash-card">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-slate-700">Criteres de selection</h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Logique :</span>
            <select
              value={logic}
              onChange={e => setLogic(e.target.value)}
              className="px-2 py-1 border border-slate-200 rounded text-xs font-medium"
              data-testid="cohort-logic-select"
            >
              <option value="AND">ET (toutes les conditions)</option>
              <option value="OR">OU (au moins une condition)</option>
            </select>
          </div>
        </div>

        {/* Date range */}
        <div className="flex gap-3 mb-4 pb-4 border-b border-slate-100">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Periode debut</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="cohort-start-date-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Periode fin</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="cohort-end-date-input" />
          </div>
        </div>

        {/* Condition rows */}
        <div className="space-y-3">
          {conditions.map((cond, idx) => {
            const fieldInfo = getFieldInfo(cond.field);
            return (
              <div key={idx} className="flex items-center gap-2" data-testid={`cohort-condition-${idx}`}>
                {idx > 0 && (
                  <span className="text-xs font-bold text-slate-400 w-8 text-center">
                    {logic}
                  </span>
                )}
                {idx === 0 && <span className="w-8" />}
                
                {/* Field select */}
                <select
                  value={cond.field}
                  onChange={e => updateCondition(idx, "field", e.target.value)}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid={`cohort-condition-field-${idx}`}
                >
                  <option value="">Choisir un critere...</option>
                  {["Patient", "Symptomes", "Constantes", "Alertes"].map(group => (
                    <optgroup key={group} label={group}>
                      {allFields.filter(f => f.group === group).map(f => (
                        <option key={f.field} value={f.field}>{f.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                {/* Operator */}
                <select
                  value={cond.operator}
                  onChange={e => updateCondition(idx, "operator", e.target.value)}
                  className="w-32 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid={`cohort-condition-operator-${idx}`}
                >
                  {getOperatorsForField(cond.field).map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>

                {/* Value */}
                {fieldInfo?.type === "select" && fieldInfo.options?.length > 0 ? (
                  <select
                    value={cond.value}
                    onChange={e => updateCondition(idx, "value", e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid={`cohort-condition-value-select-${idx}`}
                  >
                    <option value="">Valeur...</option>
                    {fieldInfo.options.map(o => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={fieldInfo?.type === "number" ? "number" : "text"}
                    value={cond.value}
                    onChange={e => updateCondition(idx, "value", e.target.value)}
                    placeholder="Valeur..."
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    min={fieldInfo?.range?.[0]}
                    max={fieldInfo?.range?.[1]}
                    data-testid={`cohort-condition-value-input-${idx}`}
                  />
                )}

                {/* Remove */}
                <button
                  onClick={() => removeCondition(idx)}
                  className="p-2 text-slate-400 hover:text-red-500"
                  disabled={conditions.length <= 1}
                  data-testid={`cohort-condition-remove-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Add condition + Run */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={addCondition}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800"
            data-testid="cohort-add-condition"
          >
            <Plus className="w-4 h-4" />
            Ajouter un critere
          </button>
          <button
            onClick={runPreview}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
            data-testid="cohort-run-btn"
          >
            <Play className="w-4 h-4" />
            {loading ? "Recherche..." : "Rechercher la cohorte"}
          </button>
        </div>
      </div>

      {/* Results */}
      {preview && (
        <div className="dash-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-slate-800">
                Resultats : {preview.cohort_size} enregistrements
              </h4>
              <p className="text-sm text-slate-500">
                {preview.unique_patients} patients uniques
              </p>
            </div>
            {preview.cohort_size > 0 && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => exportCohort("csv")}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                  data-testid="cohort-export-csv-btn"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </button>
                <button
                  onClick={() => exportCohort("json")}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                  data-testid="cohort-export-json-btn"
                >
                  <Download className="w-4 h-4" />
                  JSON
                </button>
                <button
                  onClick={() => exportWithSummary("csv")}
                  disabled={exporting}
                  data-testid="cohort-export-summary-btn"
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {exporting ? "..." : "Export + Summary (ZIP)"}
                </button>
              </div>
            )}
          </div>

          {/* Preview table */}
          {preview.records?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-medium text-slate-600">Patient</th>
                    <th className="text-left p-3 font-medium text-slate-600">Cancer</th>
                    <th className="text-left p-3 font-medium text-slate-600">Traitement</th>
                    <th className="text-center p-3 font-medium text-slate-600">Fatigue</th>
                    <th className="text-center p-3 font-medium text-slate-600">Douleur</th>
                    <th className="text-center p-3 font-medium text-slate-600">SpO2</th>
                    <th className="text-center p-3 font-medium text-slate-600">Alerte</th>
                    <th className="text-left p-3 font-medium text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.records.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-medium text-slate-800">{r.patient_name || "Anonyme"}</td>
                      <td className="p-3 text-slate-600">{r.cancer_type || "-"}</td>
                      <td className="p-3 text-slate-600">{r.treatment_type || "-"}</td>
                      <td className="p-3 text-center"><ScoreBadge score={r.fatigue} /></td>
                      <td className="p-3 text-center"><ScoreBadge score={r.pain} /></td>
                      <td className="p-3 text-center">{r.spo2 ?? "-"}</td>
                      <td className="p-3 text-center">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          r.alert_level === "ROUGE" ? "bg-red-100 text-red-700" :
                          r.alert_level === "ORANGE" ? "bg-orange-100 text-orange-700" :
                          "bg-green-100 text-green-700"
                        }`}>{r.alert_level || "-"}</span>
                      </td>
                      <td className="p-3 text-slate-500 text-xs">{r.observation_date ? new Date(r.observation_date).toLocaleDateString("fr-FR") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.cohort_size > 20 && (
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Apercu limite a 20 enregistrements sur {preview.cohort_size} total
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Aucun enregistrement ne correspond a ces criteres</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Score badge for cohort preview
function ScoreBadge({ score }) {
  if (score === null || score === undefined) return <span className="text-slate-300">-</span>;
  const colors = {
    0: "bg-green-100 text-green-700",
    1: "bg-yellow-100 text-yellow-700",
    2: "bg-orange-100 text-orange-700",
    3: "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colors[score] || "bg-slate-100 text-slate-600"}`}>
      {score}
    </span>
  );
}
